import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, ShieldCheck, Sparkles, Youtube, LogOut, Crown, Lock, CheckCircle2, Info, X,
  Map, Brain, Wrench, Rocket, FileText, Share2, PlayCircle, HelpCircle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { trackBeginCheckout, trackPurchase } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const FEATURES = [
  { icon: Map, title: "Your personalized AI roadmap", desc: "A full course sequenced from your quiz — built around your goals." },
  { icon: PlayCircle, title: "Every curated lesson unlocked", desc: "The best free YouTube teachers, hand-picked and ordered for you." },
  { icon: Brain, title: "AI summaries & quizzes", desc: "A summary and a quick quiz on every single lesson." },
  { icon: Wrench, title: "Tool-specific courses", desc: "Deep courses on ChatGPT, Midjourney, Claude, Gemini & more." },
  { icon: Rocket, title: "Practice tasks & real projects", desc: "Hands-on tasks you can actually use at work." },
  { icon: FileText, title: "Prompt library + notes export", desc: "Copy-ready prompts and export notes to Markdown / PDF." },
  { icon: HelpCircle, title: '"Ask AI" doubt-solver', desc: "Instant, lesson-aware explanations whenever you're stuck." },
  { icon: Share2, title: "Analytics & shareable roadmap", desc: "Track your streak and share your AI path in one tap." },
];

const TEASER_MODULES = [
  "AI & LLM foundations",
  "ChatGPT, Claude & Gemini mastery",
  "Prompt engineering",
  "AI for your field & profession",
  "Practice tasks & real projects",
  "Ship a real AI project",
];

function PlanCard({ id, label, price, per, sub, highlight, busy, cta = "Pay & unlock", onClick }) {
  return (
    <div
      data-testid={`plan-${id}`}
      className={`relative rounded-2xl border p-5 text-left transition-all ${
        highlight
          ? "border-primary bg-primary/[0.04] shadow-lg ring-2 ring-primary/30"
          : "border-border bg-card shadow-sm hover:border-primary/40"
      }`}
    >
      {highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-3 py-1 text-[11px] font-bold text-primary-foreground shadow-md">
          Best value · save 50%
        </span>
      )}
      <p className="font-heading text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      <div className="mt-2 flex items-end gap-1">
        <span className="font-heading text-4xl font-black tracking-tighter">₹{price}</span>
        <span className="mb-1 text-sm text-muted-foreground">/{per}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{sub}</p>
      <Button
        data-testid={`subscribe-${id}-btn`}
        onClick={() => onClick(id)}
        disabled={!!busy}
        className={`mt-4 w-full rounded-xl py-5 text-sm font-bold ${
          highlight ? "bg-primary text-primary-foreground" : "bg-foreground text-background hover:bg-foreground/90"
        }`}
      >
        {busy === id ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{cta} <Crown className="h-4 w-4" /></>}
      </Button>
    </div>
  );
}

export default function Subscribe() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRenew = searchParams.get("renew") === "1"; // extend access while still active
  const { user, checkAuth, logout } = useAuth();
  const [busy, setBusy] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null); // plan awaiting compliance confirmation
  const [billingMode, setBillingMode] = useState("once"); // "once" (one-time, default) | "auto" (subscription)

  useEffect(() => {
    // Admins never pay. Active subscribers are sent to the dashboard UNLESS they explicitly
    // chose to renew/extend (?renew=1), in which case we let them buy more time.
    if (!user) return;
    if (user.role === "admin") { navigate("/dashboard", { replace: true }); return; }
    if (user.is_subscribed && !isRenew) navigate("/dashboard", { replace: true });
  }, [user, navigate, isRenew]);

  useEffect(() => {
    if (window.Razorpay) return;
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const startCheckout = async (plan) => {
    if (!window.Razorpay) {
      toast.error("Payment library is still loading — try again in a second.");
      return;
    }
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/subscription", { plan });
      trackBeginCheckout(plan);
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "AI Pathshaalaa",
        description: plan === "yearly" ? "Yearly access — ₹3,333/year" : "Monthly access — ₹555/month",
        theme: { color: "#4F46E5" },
        prefill: { name: user?.name || "", email: user?.email || "" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            trackPurchase(plan, resp.razorpay_payment_id);
            await checkAuth();
            toast.success("You're in! Your course is unlocked 🎉");
            navigate("/dashboard", { replace: true });
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not confirm payment.");
            setBusy(null);
          }
        },
        modal: { ondismiss: () => setBusy(null) },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed. Please try again.");
        reportPaymentFailure(plan, resp);
        setBusy(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout.");
      setBusy(null);
    }
  };

  const onLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  const reportPaymentFailure = (plan, resp) => {
    api.post("/payments/checkout-issue", {
      plan,
      mode: billingMode === "auto" ? "auto-renew" : "one-time",
      code: resp?.error?.code ? String(resp.error.code) : null,
      description: resp?.error?.description || null,
      reason: resp?.error?.reason || null,
      step: resp?.error?.step || null,
    }).catch(() => {});
  };

  // One-time payment (no auto-renew, no UPI-AutoPay mandate).
  const startOrderCheckout = async (plan) => {
    if (!window.Razorpay) {
      toast.error("Payment library is still loading — try again in a second.");
      return;
    }
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/order", { plan });
      trackBeginCheckout(plan);
      const rzp = new window.Razorpay({
        key: data.key_id,
        order_id: data.order_id,
        name: "AI Pathshaalaa",
        description: plan === "yearly" ? "1 year access — ₹3,333 (one-time)" : "1 month access — ₹555 (one-time)",
        theme: { color: "#4F46E5" },
        prefill: { name: user?.name || "", email: user?.email || "" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify-order", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            trackPurchase(plan, resp.razorpay_payment_id);
            await checkAuth();
            toast.success("You're in! Your course is unlocked 🎉");
            navigate("/dashboard", { replace: true });
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not confirm payment.");
            setBusy(null);
          }
        },
        modal: { ondismiss: () => setBusy(null) },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed. Please try again.");
        reportPaymentFailure(plan, resp);
        setBusy(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout.");
      setBusy(null);
    }
  };

  // Show the compliance note BEFORE opening Razorpay.
  const requestPay = (plan) => setPendingPlan(plan);
  const confirmPay = () => {
    const plan = pendingPlan;
    setPendingPlan(null);
    if (!plan) return;
    if (billingMode === "auto") startCheckout(plan);
    else startOrderCheckout(plan);
  };


  return (
    <div className="relative min-h-screen bg-background overflow-hidden" data-testid="subscribe-page">
      {/* ambient vibrance */}
      <span className="orb top-[-8rem] left-[-6rem] h-80 w-80 bg-indigo-400 floaty-slow" />
      <span className="orb bottom-[-6rem] right-[-5rem] h-72 w-72 bg-pink-400 floaty" />

      {/* ===== Blurred, locked roadmap teaser (behind the popup) ===== */}
      <div className="absolute inset-0 grid-paper opacity-[0.4] pointer-events-none" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-16 pointer-events-none select-none blur-[3px] opacity-60">
        <p className="font-heading font-black tracking-tight text-3xl sm:text-4xl">
          🎉 Your personalized AI path is ready
        </p>
        <p className="mt-2 text-muted-foreground">Curated, sequenced and waiting — unlock it to start learning.</p>
        <div className="mt-6 space-y-3">
          {TEASER_MODULES.map((m, i) => (
            <div key={m} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
              <span className="grid place-items-center h-9 w-9 shrink-0 rounded-xl bg-primary/10 text-primary font-heading font-black">{i + 1}</span>
              <span className="flex-1 font-heading font-bold">{m}</span>
              <Lock className="h-4 w-4 text-muted-foreground" />
            </div>
          ))}
        </div>
      </div>

      {/* ===== Paywall popup ===== */}
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center overflow-y-auto bg-foreground/30 backdrop-blur-sm px-3 py-6 sm:py-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid="paywall-modal"
          className="relative w-full max-w-2xl rounded-3xl border border-border bg-card shadow-2xl"
        >
          {/* header */}
          <div className="flex items-center justify-between gap-3 border-b border-border/60 px-5 sm:px-7 py-4">
            <Logo />
            <button
              data-testid="subscribe-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>

          <div className="px-5 sm:px-7 py-6">
            <div className="text-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
                <Sparkles className="h-3.5 w-3.5" /> {isRenew ? "Renew / extend" : "One step left"}
              </span>
              <h1 className="mt-4 font-heading font-black tracking-tighter text-2xl sm:text-3xl leading-[1.1]">
                {isRenew ? "Extend your access" : "Unlock your AI path"}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
                {isRenew
                  ? "Top up more time on your plan. New time stacks on top of whatever you have left — you won't lose any days."
                  : "Pay once to unlock your curated course. The videos stay free on YouTube — you're paying for the curation, structure & Pro tools."}
              </p>
            </div>

            {user?.has_path && !isRenew && (
              <div data-testid="paywall-transition-note" className="mt-5 flex items-start gap-2.5 rounded-xl border border-amber-300/60 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-left">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                  <b>AI Pathshaalaa is now Pro.</b> A subscription is needed to continue learning — your path,
                  progress and streak are saved and will be right where you left them.
                </p>
              </div>
            )}

            {/* ===== Billing mode toggle: Pay once vs Auto-renew ===== */}
            <div className="mt-6 flex justify-center">
              <div className="inline-flex items-center rounded-full border border-border bg-secondary/60 p-1" data-testid="billing-mode-toggle">
                <button
                  data-testid="mode-once"
                  onClick={() => setBillingMode("once")}
                  className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-bold transition-all ${billingMode === "once" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Pay once
                </button>
                <button
                  data-testid="mode-auto"
                  onClick={() => setBillingMode("auto")}
                  className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-bold transition-all ${billingMode === "auto" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Auto-renew
                </button>
              </div>
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="billing-mode-note">
              {billingMode === "once"
                ? "Pay once with any UPI app, card, net-banking or wallet — no auto-renewal, no AutoPay."
                : "Auto-renews each period (UPI AutoPay / card mandate). Cancel anytime."}
            </p>
            <div data-testid="refund-guarantee-note" className="mt-3 flex items-start gap-2.5 rounded-xl border border-emerald-300/60 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-3 text-left">
              <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" />
              <p className="text-xs sm:text-[13px] text-emerald-800 dark:text-emerald-300 leading-relaxed">
                <b>7-day no-questions-asked refund policy.</b> If you don't find it value for money, it's just
                one click — and you get your money back. No forms, no calls, no questions.
              </p>
            </div>

            {/* ===== UPPER: PRICING ===== */}
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <PlanCard id="monthly" label="Monthly" price="555" per="month"
                sub={billingMode === "once" ? "1 month of full access." : "Billed monthly. Cancel anytime."}
                cta={billingMode === "auto" ? "Subscribe" : "Pay & unlock"}
                busy={busy} onClick={requestPay} />
              <PlanCard id="yearly" label="Yearly" price="3,333" per="year"
                sub={billingMode === "once" ? "1 year of full access — just ₹278/mo." : "Just ₹278/month, billed yearly."}
                cta={billingMode === "auto" ? "Subscribe" : "Pay & unlock"}
                highlight busy={busy} onClick={requestPay} />
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-2 text-xs text-muted-foreground justify-center text-center">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-success" /> Secure payments by Razorpay</span>
              <span className="inline-flex items-center gap-1.5"><Youtube className="h-4 w-4 text-[#FF0000]" /> Videos remain free & belong to their creators</span>
            </div>

            {/* ===== LOWER: PRO BENEFITS ===== */}
            <div className="mt-7 border-t border-border/60 pt-6">
              <div className="flex items-center justify-center gap-2 text-center">
                <Crown className="h-5 w-5 text-primary" />
                <h2 className="font-heading font-extrabold tracking-tight text-lg">Everything you unlock with Pro</h2>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-2.5" data-testid="pro-features">
                {FEATURES.map((f) => (
                  <div key={f.title} className="flex items-start gap-3 rounded-xl border border-border bg-background p-3">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-success" />
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-[13px] leading-tight">{f.title}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground leading-snug">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* ===== YouTube-compliance note shown BEFORE payment ===== */}
      {pendingPlan && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 backdrop-blur-sm px-4"
          data-testid="compliance-modal"
          onClick={() => setPendingPlan(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl border border-border bg-card p-6 shadow-2xl"
          >
            <button
              data-testid="compliance-close"
              onClick={() => setPendingPlan(null)}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-primary/10 text-primary">
              <Info className="h-6 w-6" />
            </span>
            <h3 className="mt-4 font-heading font-extrabold tracking-tight text-xl">
              What your payment is for
            </h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              You're paying <span className="font-semibold text-foreground">{
                billingMode === "once"
                  ? (pendingPlan === "yearly" ? "₹3,333 for 1 year" : "₹555 for 1 month")
                  : (pendingPlan === "yearly" ? "₹3,333/year (auto-renews)" : "₹555/month (auto-renews)")
              }</span> for the
              <span className="font-semibold text-foreground"> curation, structured course, AI summaries, quizzes, projects, prompts and tools</span> we
              build <span className="font-semibold text-foreground">around</span> these videos.
            </p>
            <div className="mt-3 flex items-start gap-2.5 rounded-2xl border border-border bg-background p-3">
              <Youtube className="h-5 w-5 shrink-0 text-[#FF0000]" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                You are <span className="font-bold text-foreground">not</span> paying for the videos themselves. Every video
                is <span className="font-semibold text-foreground">free on YouTube</span>, plays in the official YouTube
                player, and remains the property of its creator. We never host, download or sell the videos.
              </p>
            </div>
            <div className="mt-3 flex items-start gap-2.5 rounded-2xl border border-emerald-300/60 bg-emerald-50 dark:bg-emerald-500/10 p-3" data-testid="compliance-refund-note">
              <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600" />
              <p className="text-xs text-emerald-800 dark:text-emerald-300 leading-relaxed">
                <span className="font-bold">Covered by our 7-day no-questions-asked policy</span> — if you don't find
                it value for money, one click gets your full money back.
              </p>
            </div>
            <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2.5">
              <Button
                variant="outline"
                data-testid="compliance-cancel"
                onClick={() => setPendingPlan(null)}
                className="flex-1 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                data-testid="compliance-continue"
                onClick={confirmPay}
                className="flex-1 rounded-xl bg-primary text-primary-foreground font-bold"
              >
                Got it — continue to payment
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
